import { useState, useEffect, useCallback } from "react";
import { ArrowRight, ExternalLink, AlertTriangle, BookOpen } from "lucide-react";
import { clp as fmt } from "../utils/format";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const pct = n => (n ?? 0).toFixed(1) + "%";
const authFetch = url => fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── Design system (igual que Pulso) ─────────────────────────────────────────
const DS = {
  green:  "#059669", greenBg:  "#ECFDF5", greenBorder:  "rgba(5,150,105,0.15)",
  red:    "#DC2626", redBg:    "#FEF2F2", redBorder:    "rgba(220,38,38,0.15)",
  amber:  "#D97706", amberBg:  "#FFFBEB", amberBorder:  "rgba(217,119,6,0.15)",
  indigo: "#4338CA", indigoBg: "#EEF2FF", indigoBorder: "rgba(67,56,202,0.15)",
  lbl:    "#9496B4",
  border: "#E4E6F5",
  shadow: "0 4px 20px rgba(67,56,202,0.08)",
  mono:   "'JetBrains Mono', monospace",
};

// ── Alerta individual ────────────────────────────────────────────────────────
// items puede ser string (legacy) o {label, onClick, ctaLabel, external}.
function Alerta({ tipo, titulo, items, badge }) {
  const c = {
    danger: { bg: DS.redBg,   border: DS.redBorder,   title: DS.red,   dot: DS.red   },
    warn:   { bg: DS.amberBg, border: DS.amberBorder, title: DS.amber, dot: DS.amber },
    info:   { bg: DS.indigoBg,border: DS.indigoBorder,title: DS.indigo,dot: DS.indigo},
  }[tipo] || {};
  if (!items.length) return null;
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10,
      padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: c.title }}>{titulo}</div>
        {badge && (
          <div style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
            background: c.dot + "20", color: c.dot }}>{badge}</div>
        )}
      </div>
      {items.map((item, i) => {
        const isObj = item && typeof item === "object";
        const label = isObj ? item.label : item;
        const onClick = isObj ? item.onClick : null;
        const ctaLabel = isObj ? item.ctaLabel : null;
        const external = isObj ? item.external : false;

        return (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "5px 0",
            borderTop: i > 0 ? `1px dashed ${c.border}` : "none",
          }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: c.dot,
              flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12, color: "#555", lineHeight: 1.5 }}>
              {label}
            </div>
            {onClick && (
              <button
                onClick={onClick}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: c.dot + "15", color: c.dot,
                  border: `1px solid ${c.dot}30`, borderRadius: 7,
                  padding: "3px 9px", fontSize: 11, fontWeight: 700,
                  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = c.dot + "25"; }}
                onMouseLeave={e => { e.currentTarget.style.background = c.dot + "15"; }}
              >
                {ctaLabel || "Ver"}
                {external ? <ExternalLink size={11} strokeWidth={2.5} /> : <ArrowRight size={11} strokeWidth={2.5} />}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Fila de desglose ROAS ────────────────────────────────────────────────────
function FilaDesglose({ label, value, bold, color, indent }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      paddingLeft: indent ? 20 : 0,
      padding: bold ? "6px 0" : "3px 0",
      borderTop: bold ? `1px solid rgba(217,119,6,0.15)` : "none",
      marginTop: bold ? 4 : 0,
    }}>
      <span style={{ fontSize: 12, color: bold ? "#1E1E2E" : "#6B7280", fontWeight: bold ? 700 : 400 }}>
        {label}
      </span>
      <span style={{
        fontFamily: DS.mono, fontSize: 12,
        fontWeight: bold ? 700 : 400,
        color: color || (bold ? "#1E1E2E" : "#6B7280"),
      }}>
        {value}
      </span>
    </div>
  );
}

// ── Bloque explicativo ROAS ──────────────────────────────────────────────────
function BloqueRoas({ d, roasNum }) {
  const [expandido, setExpandido] = useState(false);

  const ventaTotal        = d.venta_total        ?? 0;
  const fleteEntregados   = d.flete_entregados   ?? 0;
  const costoProveedor    = d.costo_proveedor    ?? 0;
  const gastoMeta         = d.gasto_meta         ?? 0;
  const fleteDevoluciones = d.flete_devoluciones ?? 0;
  const gananciaReal      = ventaTotal - fleteEntregados - costoProveedor - gastoMeta - fleteDevoluciones;
  const roasLabel         = roasNum != null ? roasNum.toFixed(2) : "—";
  const gananciaXPeso     = roasNum != null ? roasNum.toFixed(2) : "—";

  return (
    <div style={{
      background: roasNum != null && roasNum < 1
        ? `linear-gradient(135deg, ${DS.redBg} 0%, ${DS.amberBg} 100%)`
        : DS.amberBg,
      border: `1px solid ${DS.amberBorder}`,
      borderRadius: 12,
      padding: "14px 16px",
    }}>
      {/* Cabecera */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: DS.amber, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <AlertTriangle size={14} strokeWidth={2.5} />
          ROAS bajo el benchmark del nicho
        </div>
        <div style={{ fontFamily: DS.mono, fontSize: 12, fontWeight: 700, padding: "2px 10px",
          borderRadius: 20, background: DS.amberBg, color: DS.amber,
          border: `1px solid ${DS.amberBorder}` }}>
          {roasLabel}x actual
        </div>
      </div>

      {/* Descripción corta */}
      <div style={{ fontSize: 13, color: "#555", lineHeight: 1.55, marginBottom: 12 }}>
        El estándar saludable para dropshipping COD en LATAM es{" "}
        <strong style={{ color: "#1E1E2E" }}>1.5x mínimo</strong>. Con el ROAS actual,
        por cada $1 invertido en Meta tu ganancia real es{" "}
        <strong style={{ fontFamily: DS.mono }}>${roasLabel}</strong>.
        {roasNum != null && roasNum < 1 && (
          <span style={{ color: DS.red, fontWeight: 600 }}> La pauta está consumiendo más de lo que genera.</span>
        )}
      </div>

      {/* Botón expandir */}
      <button
        onClick={() => setExpandido(v => !v)}
        style={{
          fontSize: 12, fontWeight: 600, color: DS.amber,
          background: "rgba(217,119,6,0.08)", border: `1px solid ${DS.amberBorder}`,
          borderRadius: 8, padding: "5px 12px", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit",
          marginBottom: expandido ? 14 : 0,
        }}
      >
        {expandido ? "▲" : "▼"} {expandido ? "Ocultar desglose" : "¿Cómo se calculó este ROAS?"}
      </button>

      {/* Desglose detallado */}
      {expandido && (
        <div style={{
          background: "rgba(255,255,255,0.7)", borderRadius: 10,
          border: `1px solid ${DS.amberBorder}`, padding: "14px 16px",
          marginTop: 2,
        }}>
          {/* Fórmula principal */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: DS.lbl, textTransform: "uppercase",
              letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>
              Fórmula
            </div>
            <div style={{ fontFamily: DS.mono, fontSize: 13, color: "#1E1E2E", lineHeight: 1.8 }}>
              <span style={{ color: DS.amber, fontWeight: 700 }}>ROAS</span>
              {" = Ganancia Real ÷ Gasto Meta Ads"}
            </div>
            <div style={{ fontFamily: DS.mono, fontSize: 13, color: "#1E1E2E", lineHeight: 1.8, paddingLeft: 8 }}>
              {"= "}{fmt(gananciaReal)}{" ÷ "}{fmt(gastoMeta)}{" = "}
              <span style={{ color: DS.amber, fontWeight: 700 }}>{roasLabel}x</span>
            </div>
          </div>

          {/* Desglose Ganancia Real */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: DS.lbl, textTransform: "uppercase",
              letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>
              Desglose de Ganancia Real
            </div>
            <FilaDesglose label="Venta Total (pedidos entregados)" value={fmt(ventaTotal)} color={DS.green} />
            <FilaDesglose label="− Costo flete entregados" value={"−" + fmt(fleteEntregados)} color={DS.red} />
            <FilaDesglose label="− Costo proveedor" value={"−" + fmt(costoProveedor)} color={DS.red} />
            <FilaDesglose label="− Gasto Meta Ads" value={"−" + fmt(gastoMeta)} color={DS.red} />
            <FilaDesglose label="− Fletes fallidos / devoluciones" value={"−" + fmt(fleteDevoluciones)} color={DS.red} />
            <FilaDesglose label="= Ganancia Real" value={fmt(gananciaReal)} bold color={gananciaReal >= 0 ? DS.green : DS.red} />
          </div>

          {/* Qué significa */}
          <div style={{
            background: DS.indigoBg, border: `1px solid ${DS.indigoBorder}`,
            borderRadius: 8, padding: "12px 14px",
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: DS.indigo, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <BookOpen size={14} strokeWidth={2.5} />
              ¿Qué significa el ROAS?
            </div>
            <div style={{ fontSize: 12, color: "#555", lineHeight: 1.65, marginBottom: 8 }}>
              Es cuántos pesos de <strong>ganancia neta real</strong> generás por cada peso invertido en Meta Ads.
              La ganancia ya descuenta todos los costos: proveedor, fletes, publicidad y devoluciones.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {[
                { val: "1.0x", desc: "por cada $1 en Meta, tu ganancia real es $1,00 — la pauta solo cubre sus propios costos", color: DS.amber },
                { val: "1.5x", desc: "mínimo saludable — por cada $1 en Meta, tu ganancia real es $1,50", color: DS.green },
                { val: `${roasLabel}x`, desc: `tu resultado actual — por cada $1 en Meta, tu ganancia real es $${gananciaXPeso}`, color: roasNum != null && roasNum < 1 ? DS.red : DS.amber, bold: true },
              ].map(row => (
                <div key={row.val} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12 }}>
                  <span style={{
                    fontFamily: DS.mono, fontWeight: 700, color: row.color,
                    minWidth: 34, flexShrink: 0,
                    background: row.bold ? row.color + "18" : "transparent",
                    padding: "1px 6px", borderRadius: 6,
                  }}>{row.val}</span>
                  <span style={{ color: "#555", lineHeight: 1.5, fontWeight: row.bold ? 600 : 400 }}>
                    {row.desc}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Alertas({ desde, hasta, productos, onNavegar }) {
  const [datos,    setDatos]    = useState(null);
  const [cargando, setCargando] = useState(false);

  const fetchDatos = useCallback(async (d, h) => {
    if (!d || !h) return;
    setCargando(true);
    try {
      const res = await authFetch(`${API_URL}/api/pulso?desde=${d}&hasta=${h}`);
      if (res.ok) setDatos(await res.json());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { fetchDatos(desde, hasta); }, [desde, hasta, fetchDatos]);

  if (cargando && !datos) return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, color: DS.lbl,
      fontSize: 13, padding: "3rem 0" }}>
      <div style={{ width: 18, height: 18, border: `2px solid ${DS.border}`,
        borderTop: `2px solid ${DS.indigo}`, borderRadius: "50%",
        animation: "spin 0.8s linear infinite" }} />
      Cargando alertas...
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!datos) return (
    <div style={{ color: DS.lbl, fontSize: 13, padding: "3rem 0" }}>Sin datos disponibles.</div>
  );

  const d = datos || {};

  // ── Cálculos ─────────────────────────────────────────────────────────────
  const ventaTotal        = d.venta_total        ?? 0;
  const fleteEntregados   = d.flete_entregados   ?? 0;
  const costoProveedor    = d.costo_proveedor    ?? 0;
  const gastoMeta         = d.gasto_meta         ?? 0;
  const fleteDevoluciones = d.flete_devoluciones ?? 0;

  // Para ROAS usamos la misma base que el desglose: pedidos (ganancia real basada en pedidos)
  const gananciaReal = ventaTotal - fleteEntregados - costoProveedor - gastoMeta - fleteDevoluciones;
  const roasNum      = gastoMeta > 0 ? gananciaReal / gastoMeta : null;

  // Alertas con CTAs contextuales: cada item navega a la vista relevante (DetalleProducto,
  // Transportadoras) o abre Meta Ads Manager para ROAS bajo.
  const goToProducto = (nombre) => {
    if (!onNavegar) return;
    // Navegar a DetalleProducto con el nombre como query/filtro.
    // El componente DetalleProducto ya soporta ?producto= en su lógica interna.
    try { sessionStorage.setItem("producto_filtro_inicial", nombre); } catch {}
    onNavegar("detalle-producto");
  };
  const goToTransportadoras = () => onNavegar && onNavegar("transportadoras");
  const openMetaAdsManager = () => window.open("https://business.facebook.com/adsmanager/manage/campaigns", "_blank", "noopener,noreferrer");

  const alertaRoas     = roasNum != null && roasNum < 1.5;
  const margenNegativo = (productos || []).filter(p => p.margen_real < 0).map(p => ({
    label: `${p.nombre} — margen ${fmt(p.margen_real)}`,
    onClick: () => goToProducto(p.nombre),
    ctaLabel: "Ver producto",
  }));
  const roasBajo       = (productos || []).filter(p => p.gasto_meta > 0 && p.roas < 1).map(p => ({
    label: `${p.nombre} — ROAS ${(p.roas ?? 0).toFixed(2)}x`,
    onClick: openMetaAdsManager,
    ctaLabel: "Meta Ads",
    external: true,
  }));
  const altaDev        = (productos || []).filter(p => p.total_confirmados >= 5 && p.tasa_entrega < 60).map(p => ({
    label: `${p.nombre} — entrega ${pct(p.tasa_entrega)}`,
    onClick: goToTransportadoras,
    ctaLabel: "Ver transportadoras",
  }));

  // Trampa: producto entrega bien (NDR < 30%) pero la audiencia infla los costos
  // (factor de inflación > 1.20x = TC < 83.3%). Se ve "ganador" por entrega pero
  // Meta está cobrando audiencia sucia. Solo aplica con gasto en Meta y volumen mínimo.
  const trampa = (productos || []).filter(p => {
    if (!(p.gasto_meta > 0)) return false;
    if ((p.total_pedidos ?? 0) < 10) return false;
    const tc = p.tasa_confirmacion ?? 0;
    const te = p.tasa_entrega      ?? 0;
    if (tc <= 0) return false;
    const factor = 100 / tc;
    const ndr    = 100 - te;
    return factor > 1.20 && ndr < 30;
  }).map(p => {
    const tc = p.tasa_confirmacion ?? 0;
    const factor = tc > 0 ? (100 / tc).toFixed(2) : "—";
    const teLabel = pct(p.tasa_entrega ?? 0);
    return {
      label: `${p.nombre} — ${factor}x leads por venta · ${teLabel} de entrega`,
      onClick: () => goToProducto(p.nombre),
      ctaLabel: "Ver producto",
    };
  });

  const sinAlertas     = !alertaRoas && !margenNegativo.length && !roasBajo.length && !altaDev.length && !trampa.length;
  const numAlertas     = (alertaRoas ? 1 : 0) + margenNegativo.length + roasBajo.length + altaDev.length + trampa.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Período */}
      {d.desde && (
        <div style={{ fontSize: 11, color: DS.lbl, display: "flex", alignItems: "center", gap: 8 }}>
          {cargando && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 10, height: 10, border: `1.5px solid ${DS.border}`,
                borderTop: `1.5px solid ${DS.indigo}`, borderRadius: "50%",
                animation: "spin 0.8s linear infinite" }} />
              Actualizando...
            </span>
          )}
          <span>{d.desde} → {d.hasta}</span>
          <span style={{ background: DS.indigoBg, color: DS.indigo, fontFamily: DS.mono,
            fontSize: 11, fontWeight: 600, padding: "1px 8px", borderRadius: 20 }}>
            {d.total_pedidos ?? 0} pedidos
          </span>
        </div>
      )}

      {/* Resumen de estado */}
      <div style={{
        background: sinAlertas ? DS.greenBg : DS.amberBg,
        border: `1px solid ${sinAlertas ? DS.greenBorder : DS.amberBorder}`,
        borderRadius: 12, padding: "16px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 22 }}>{sinAlertas ? "✅" : "🚨"}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: sinAlertas ? DS.green : DS.amber }}>
              {sinAlertas ? "Todo en orden" : `${numAlertas} alerta${numAlertas > 1 ? "s" : ""} activa${numAlertas > 1 ? "s" : ""}`}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
              {sinAlertas
                ? "No se detectaron problemas críticos para el período seleccionado."
                : "Revisá los detalles abajo para tomar acción."}
            </div>
          </div>
        </div>
        {!sinAlertas && (
          <div style={{
            fontFamily: DS.mono, fontSize: 28, fontWeight: 800,
            color: DS.amber, lineHeight: 1,
          }}>{numAlertas}</div>
        )}
      </div>

      {/* Alertas */}
      {!sinAlertas && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* ROAS con desglose detallado */}
          {alertaRoas && <BloqueRoas d={d} roasNum={roasNum} />}

          <Alerta tipo="danger"
            titulo={`${margenNegativo.length} producto(s) con margen negativo`}
            badge={`${margenNegativo.length} afectados`}
            items={margenNegativo} />
          <Alerta tipo="warn"
            titulo={`${roasBajo.length} campaña(s) con ROAS menor a 1x`}
            badge="Revisar pauta"
            items={roasBajo} />
          <Alerta tipo="info"
            titulo={`${altaDev.length} producto(s) con entrega < 60%`}
            badge="Tasa baja"
            items={altaDev} />
          <Alerta tipo="info"
            titulo={`${trampa.length} producto(s) con audiencia inflada`}
            badge="Pauta cara, entrega ok"
            items={trampa} />
        </div>
      )}
    </div>
  );
}
